import React, { useEffect, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, CheckCheck, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { useWhatsAppMessages } from '@/hooks/whatsapp';
import type { WhatsAppContactWithDetails, WhatsAppMessage } from '@/types/whatsapp';
import { cn } from '@/lib/core/utils';

interface ChatThreadProps {
  businessId: string | null;
  contact: WhatsAppContactWithDetails;
}

export function ChatThread({ businessId, contact }: ChatThreadProps) {
  const {
    messages,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
  } = useWhatsAppMessages(businessId, contact.id);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (contact.unread_count > 0) {
      markAsRead();
    }
  }, [contact.id, contact.unread_count, markAsRead]);

  const formatMessageTime = (timestamp: string): string => {
    return format(new Date(timestamp), 'HH:mm');
  };

  const formatDateSeparator = (timestamp: string): string => {
    const date = new Date(timestamp);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMMM d, yyyy');
  };

  const shouldShowDateSeparator = (message: WhatsAppMessage, index: number): boolean => {
    if (index === 0) return true;
    const prevMessage = messages[index - 1];
    return !isSameDay(new Date(message.sent_at), new Date(prevMessage.sent_at));
  };

  const getStatusIcon = (message: WhatsAppMessage) => {
    if (message.direction !== 'outbound') return null;

    switch (message.status) {
      case 'pending':
        return <Clock className="h-3 w-3 text-gray-400" />;
      case 'sent':
        return <Check className="h-3 w-3 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-gray-400" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-blue-500" />;
      case 'failed':
        return <AlertCircle className="h-3 w-3 text-red-500" />;
      default:
        return null;
    }
  };

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className={cn('flex', i % 2 === 0 ? 'justify-start' : 'justify-end')}>
            <Skeleton className={cn('h-16 rounded-lg', i % 2 === 0 ? 'w-3/4' : 'w-1/2')} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <ScrollArea className="h-full" ref={scrollRef}>
      <div className="flex flex-col gap-1 p-4">
        {/* Load more button */}
        {hasMore && (
          <div className="mb-4 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadMore}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Load older messages
            </Button>
          </div>
        )}

        {messages.map((message, index) => (
          <React.Fragment key={message.id}>
            {/* Date separator */}
            {shouldShowDateSeparator(message, index) && (
              <div className="my-4 flex items-center justify-center">
                <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                  {formatDateSeparator(message.sent_at)}
                </span>
              </div>
            )}

            {/* Message bubble */}
            <div
              className={cn(
                'flex',
                message.direction === 'outbound' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={cn(
                  'max-w-[75%] rounded-lg px-3 py-2',
                  message.direction === 'outbound'
                    ? 'bg-green-600 text-white'
                    : 'bg-muted'
                )}
              >
                {/* Message type indicator for non-text messages */}
                {message.message_type !== 'text' && message.message_type !== 'template' && (
                  <div className="mb-1 text-xs opacity-70">
                    [{message.message_type}]
                  </div>
                )}

                {/* Template indicator */}
                {message.message_type === 'template' && message.template_name && (
                  <div className="mb-1 text-xs opacity-70">
                    Template: {message.template_name}
                  </div>
                )}

                {/* Message content */}
                {message.content && (
                  <p className="whitespace-pre-wrap break-words text-sm">
                    {message.content}
                  </p>
                )}

                {/* Media */}
                {message.media_url && (
                  <div className="mt-1">
                    {message.message_type === 'image' ? (
                      <img
                        src={message.media_url}
                        alt="Shared image"
                        className="max-h-60 rounded"
                      />
                    ) : message.message_type === 'document' ? (
                      <a
                        href={message.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded bg-black/10 p-2 text-sm hover:bg-black/20"
                      >
                        <span>📄</span>
                        <span>{message.media_filename || 'Document'}</span>
                      </a>
                    ) : (
                      <a
                        href={message.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm underline"
                      >
                        View media
                      </a>
                    )}
                  </div>
                )}

                {/* Time and status */}
                <div className={cn(
                  'mt-1 flex items-center justify-end gap-1 text-xs',
                  message.direction === 'outbound' ? 'text-white/70' : 'text-muted-foreground'
                )}>
                  <span>{formatMessageTime(message.sent_at)}</span>
                  {getStatusIcon(message)}
                </div>

                {/* Error message */}
                {message.status === 'failed' && message.error_message && (
                  <div className="mt-1 text-xs text-red-300">
                    Failed: {message.error_message}
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        ))}

        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  );
}
